(() => {
  const ROOT_SELECTOR = '[data-fx-product]';

  function parseJson(root) {
    const jsonNode = root.querySelector('[data-product-variants-json]');
    if (!jsonNode) return [];

    try {
      return JSON.parse(jsonNode.textContent || '[]');
    } catch (error) {
      console.error('Funginix product section: invalid variant JSON.', error);
      return [];
    }
  }

  function getPlainMoneyText(value) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = String(value ?? '');
    return (wrapper.textContent || wrapper.innerText || '').trim();
  }

  function initProductSection(root) {
    if (!root || root.dataset.fxInitialized === 'true') return;
    root.dataset.fxInitialized = 'true';

    const variants = parseJson(root);
    const variantById = new Map(variants.map((variant) => [String(variant.id), variant]));

    const variantInput = root.querySelector('[data-variant-id-input]');
    const variantRadios = Array.from(root.querySelectorAll('[data-variant-radio]'));
    const variantCards = Array.from(root.querySelectorAll('[data-variant-card]'));
    const addButton = root.querySelector('[data-add-to-cart]');
    const addButtonText = root.querySelector('[data-add-to-cart-text]');

    const mediaItems = Array.from(root.querySelectorAll('[data-media-id]'));
    const thumbnails = Array.from(root.querySelectorAll('[data-media-thumbnail]'));
    const previousButton = root.querySelector('[data-media-prev]');
    const nextButton = root.querySelector('[data-media-next]');

    const subscriptionToggle = root.querySelector('[data-subscription-toggle]');
    const sellingPlanSelect = root.querySelector('[data-selling-plan-select]');
    const sellingPlanInput = root.querySelector('[data-selling-plan-input]');

    const quantityInput = root.querySelector('[data-quantity-input]');
    const quantityMinus = root.querySelector('[data-quantity-minus]');
    const quantityPlus = root.querySelector('[data-quantity-plus]');

    const lightbox = root.querySelector('[data-lightbox]');
    const lightboxImage = root.querySelector('[data-lightbox-image]');
    const lightboxClose = root.querySelector('[data-lightbox-close]');

    function getSelectedVariant() {
      return variantById.get(String(variantInput?.value || '')) || variants[0] || null;
    }

    function activateMedia(mediaId) {
      if (!mediaId || mediaItems.length === 0) return;
      const targetId = String(mediaId);
      let matched = false;

      mediaItems.forEach((item) => {
        const active = item.dataset.mediaId === targetId;
        item.hidden = !active;
        item.classList.toggle('is-active', active);
        if (active) matched = true;

        if (!active) {
          item.querySelectorAll('video').forEach((video) => {
            if (!video.paused) video.pause();
          });
        }
      });

      if (!matched) return;

      thumbnails.forEach((thumbnail) => {
        const active = thumbnail.dataset.mediaThumbnail === targetId;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
        if (active) {
          thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      });
    }

    function stepMedia(direction) {
      if (mediaItems.length < 2) return;
      const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
      const safeIndex = activeIndex < 0 ? 0 : activeIndex;
      const nextIndex = (safeIndex + direction + mediaItems.length) % mediaItems.length;
      activateMedia(mediaItems[nextIndex].dataset.mediaId);
    }

    function findAllocation(variant, preferredPlanId) {
      if (!variant || !Array.isArray(variant.selling_plan_allocations)) return null;
      if (variant.selling_plan_allocations.length === 0) return null;

      return (
        variant.selling_plan_allocations.find(
          (allocation) => String(allocation.selling_plan_id) === String(preferredPlanId)
        ) || variant.selling_plan_allocations[0]
      );
    }

    function subscriptionIsActive() {
      return Boolean(subscriptionToggle?.checked);
    }

    function syncSellingPlan(variant) {
      if (!sellingPlanInput) return null;

      if (!subscriptionIsActive()) {
        sellingPlanInput.value = '';
        sellingPlanInput.disabled = true;
        return null;
      }

      const preferredPlanId = sellingPlanSelect?.value;
      const allocation = findAllocation(variant, preferredPlanId);

      if (!allocation) {
        sellingPlanInput.value = '';
        sellingPlanInput.disabled = true;
        return null;
      }

      sellingPlanInput.value = String(allocation.selling_plan_id);
      sellingPlanInput.disabled = false;

      if (sellingPlanSelect && sellingPlanSelect.value !== String(allocation.selling_plan_id)) {
        sellingPlanSelect.value = String(allocation.selling_plan_id);
      }

      return allocation;
    }

    function updateAllCardPrices() {
      const useSubscriptionPrice = subscriptionIsActive();
      const preferredPlanId = sellingPlanSelect?.value;

      variantCards.forEach((card) => {
        const variant = variantById.get(String(card.dataset.variantId));
        if (!variant) return;

        const allocation = useSubscriptionPrice ? findAllocation(variant, preferredPlanId) : null;
        const priceNode = card.querySelector('[data-card-price]');
        const compareNode = card.querySelector('[data-card-compare]');

        if (priceNode) {
          priceNode.textContent = getPlainMoneyText(allocation?.price_formatted || variant.price_formatted);
        }

        if (compareNode) {
          const shouldShowCompare = Number(variant.compare_at_price) > Number(variant.price);
          compareNode.hidden = !shouldShowCompare;
          if (shouldShowCompare) compareNode.textContent = getPlainMoneyText(variant.compare_at_price_formatted);
        }
      });
    }

    function setSelectedVariant(variantId, options = {}) {
      const variant = variantById.get(String(variantId));
      if (!variant || !variantInput) return;

      variantInput.value = String(variant.id);

      variantCards.forEach((card) => {
        card.classList.toggle('is-selected', card.dataset.variantId === String(variant.id));
      });

      variantRadios.forEach((radio) => {
        radio.checked = radio.value === String(variant.id);
      });

      if (addButton) addButton.disabled = !variant.available;
      if (addButtonText && addButton) {
        addButtonText.textContent = variant.available
          ? addButton.dataset.availableLabel || 'Add To Cart'
          : addButton.dataset.soldOutLabel || 'Sold out';
      }

      syncSellingPlan(variant);
      updateAllCardPrices();

      if (variant.featured_media_id) activateMedia(variant.featured_media_id);

      if (options.updateUrl !== false && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url.toString());
      }

      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      root.dispatchEvent(
        new CustomEvent('funginix:variant-change', {
          bubbles: true,
          detail: { variant }
        })
      );
    }

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => activateMedia(thumbnail.dataset.mediaThumbnail));
    });

    previousButton?.addEventListener('click', () => stepMedia(-1));
    nextButton?.addEventListener('click', () => stepMedia(1));

    variantRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.checked) setSelectedVariant(radio.value);
      });
    });

    subscriptionToggle?.addEventListener('change', () => {
      syncSellingPlan(getSelectedVariant());
      updateAllCardPrices();
    });

    sellingPlanSelect?.addEventListener('change', () => {
      syncSellingPlan(getSelectedVariant());
      updateAllCardPrices();
    });

    quantityMinus?.addEventListener('click', () => {
      if (!quantityInput) return;
      const current = Number.parseInt(quantityInput.value, 10) || 1;
      quantityInput.value = String(Math.max(1, current - 1));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    });

    quantityPlus?.addEventListener('click', () => {
      if (!quantityInput) return;
      const current = Number.parseInt(quantityInput.value, 10) || 1;
      quantityInput.value = String(current + 1);
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    });

    quantityInput?.addEventListener('change', () => {
      const safeValue = Math.max(1, Number.parseInt(quantityInput.value, 10) || 1);
      quantityInput.value = String(safeValue);
    });

    root.querySelectorAll('[data-zoom-src]').forEach((zoomButton) => {
      zoomButton.addEventListener('click', () => {
        if (!lightbox || !lightboxImage) return;
        lightboxImage.src = zoomButton.dataset.zoomSrc || '';
        lightboxImage.alt = zoomButton.querySelector('img')?.alt || '';

        if (typeof lightbox.showModal === 'function') {
          lightbox.showModal();
        } else {
          lightbox.setAttribute('open', '');
        }
      });
    });

    lightboxClose?.addEventListener('click', () => lightbox?.close());
    lightbox?.addEventListener('click', (event) => {
      if (event.target === lightbox) lightbox.close();
    });

    const initialVariant = getSelectedVariant();
    if (initialVariant) {
      setSelectedVariant(initialVariant.id, { updateUrl: false });
    }
  }

  function initializeAll(container = document) {
    container.querySelectorAll(ROOT_SELECTOR).forEach(initProductSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
